import React, { useEffect, useState } from 'react';
import { getApolloClient } from '@core/lib/getApolloClient';
import { INVITE_USER } from '@core/invite/invite.gql';
import { Site, SiteStatus, UserRole } from '@core/generated/gql-types';
import { useSiteStore } from '@core/site/useSiteStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useTenantId } from '@/contexts/TenantContext';
import { SiteMultiSelect } from '@/components/site/SiteMultiSelect';
import {
  actionModalInputClassName,
  actionPrimaryButtonClassName,
} from '@core/theme/actionTheme.util';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  onSuccess?: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  clientId,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(UserRole.ClientUser);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tenantId = useTenantId();
  const { getTerm } = useTemplateStore();
  const { fetchSitesForClient, getSitesForClient } = useSiteStore();

  useEffect(() => {
    if (!isOpen || !clientId) return;
    fetchSitesForClient(clientId);
  }, [isOpen, clientId, fetchSitesForClient]);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setRole(UserRole.ClientUser);
      setSelectedSiteIds([]);
      setError(null);
      setHasAttemptedSubmit(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (role !== UserRole.ClientUser) {
      setSelectedSiteIds([]);
    }
  }, [role]);

  const activeSites = getSitesForClient(clientId)
    .filter((site: Site) => site.status === SiteStatus.Active)
    .sort((a, b) => a.name.localeCompare(b.name));

  const requiresSiteSelection = role === UserRole.ClientUser;
  const isMissingSiteSelection =
    requiresSiteSelection && selectedSiteIds.length === 0;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasAttemptedSubmit(true);

    if (isMissingSiteSelection) {
      setError('Please assign at least one site before inviting this user.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await getApolloClient().mutate({
        mutation: INVITE_USER,
        variables: {
          email,
          role,
          clientId,
          tenantId,
          siteIds:
            role === UserRole.ClientUser && selectedSiteIds.length > 0
              ? selectedSiteIds
              : undefined,
        },
      });
      if (data?.inviteUser) {
        onSuccess?.();
        onClose();
      } else {
        setError('Failed to send invite');
      }
      console.log(
        `submitting invite for ${email} with role ${role} and siteIds ${selectedSiteIds}`,
      );
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'An unexpected error occurred',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-[500px] rounded-lg bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800">Invite New User</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={`${actionModalInputClassName} text-black`}
              placeholder="Enter email address"
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="role"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className={`${actionModalInputClassName} text-black`}
            >
              <option value={UserRole.ClientUser}>
                {getTerm(TemplateTerm.CLIENT, false)} User
              </option>
              <option value={UserRole.ClientAdmin}>
                {getTerm(TemplateTerm.CLIENT, false)} Admin
              </option>
            </select>
          </div>

          {role === UserRole.ClientUser ? (
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Sites
              </label>
              <p className="mb-2 text-xs text-gray-500">
                Choose which sites this user can view and post jobs to.
              </p>
              <SiteMultiSelect
                sites={activeSites}
                selectedSiteIds={selectedSiteIds}
                onChange={setSelectedSiteIds}
                disabled={isSubmitting}
              />
              {hasAttemptedSubmit && isMissingSiteSelection && (
                <p className="mt-2 text-xs text-red-600">
                  At least one site is required for this user.
                </p>
              )}
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-[var(--action-color)] focus:ring-offset-2 focus:outline-hidden`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`rounded-md border border-transparent px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionPrimaryButtonClassName}`}
            >
              {isSubmitting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
